import React, { useState, useContext, useEffect } from 'react';
import { NavigationSidebarContext } from '../../contexts/navigationSidebar';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChartPieIcon, UsersIcon, CurrencyDollarIcon, MapPinIcon, TruckIcon, ArrowsRightLeftIcon, BuildingStorefrontIcon, CogIcon, FingerPrintIcon, Bars4Icon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { AuthContext } from '../../contexts/auth';
import { useTranslation } from 'react-i18next';
import { DefaultViews, RoleBasedViews } from '../../pages/views';

// Reusable SidebarItem component with dropdown support
const SidebarItem = ({ Icon, label, to, isActive, onClick, children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();

    if (children) {
        return (
            <div className="space-y-1">
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className={`w-full ${isActive ? 'text-white duration-300 bg-pink-700 hover:bg-pink-700' : 'text-gray-400'} font-semibold transition-all hover:text-white px-6 rounded-md hover:bg-slate-400 py-2 flex items-center justify-between`}
                >
                    <div className="flex items-center space-x-2">
                        {Icon && <Icon className="h-5 w-5" />}
                        <span className="flex truncate">{label}</span>
                    </div>
                    <ChevronDownIcon className={`h-4 w-4 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                </button>
                {isOpen && (
                    <div className="ml-6 space-y-1">
                        {children.map((child, index) => (
                            <Link
                                key={index}
                                to={child.path}
                                onClick={onClick}
                                className={`${location.pathname === child.path ? 'text-white bg-pink-700' : 'text-gray-400'} font-semibold block transition-all hover:text-white px-6 rounded-md hover:bg-slate-400 py-2 flex items-center space-x-2`}
                            >
                                {child.icon && <child.icon className="h-5 w-5" />}
                                <span className="flex truncate">{child.label}</span>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    return (
        <Link to={to} onClick={onClick} className={`${isActive ? 'text-white duration-300 bg-pink-700 hover:bg-pink-700' : 'text-gray-400'} font-semibold transition-all hover:text-white px-6 rounded-md hover:bg-slate-400 py-2 flex items-center space-x-2`}>
            {Icon && <Icon className="h-5 w-5" />}
            <div className="flex truncate">{label}</div>
        </Link>
    );
};

function NavigationSidebar() {
    const { isNavigationSidebarOpen, setNavigationSidebarOpen } = useContext(NavigationSidebarContext);
    const { user } = useContext(AuthContext);
    const { roles } = useContext(AuthContext);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const { t, i18n } = useTranslation('global');
    const location = useLocation();
    const navigate = useNavigate();
    const [encodedRoles, setEncodedRoles] = useState(0);

    useEffect(() => {
        let encodedValue = 0;
        switch (roles) {
            case 'a4288f73-64cc-44de-b889-7a777041a26e': // station manager
                encodedValue |= 1;
                break;
            case '795717d5-a27f-471c-b20c-a4ef7cfbb1ec': // cheche support
                encodedValue |= 2;
                break;
            default:
                encodedValue |= 3;
        }
        setEncodedRoles(encodedValue);
    }, [roles]);


    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        }

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        }
    }, [])

    const isMobile = screenWidth < 768;
    const isTablet = screenWidth >= 768 && screenWidth <= 1024;
    const isDesktop = screenWidth > 1024;


    const handleLinkClick = () => {
        if (isMobile) {
            setNavigationSidebarOpen(!isNavigationSidebarOpen);
        }
    };

    const roles_menu = Object.keys(RoleBasedViews[user.roles[0]].routes).map((key) => {
        const { icon, label, children } = RoleBasedViews[user.roles[0]].routes[key];
        return { Icon: icon, label, to: key, children };
    });

    const default_menu = Object.keys(DefaultViews).map((key) => {
        const { icon, label } = DefaultViews[key];
        return { Icon: icon, label, to: key };
    });
    default_menu.forEach((defaultRoute) => {
        const existsInRoles = roles_menu.find((roleRoute) => roleRoute.to === defaultRoute.to);
        if (!existsInRoles) {
            roles_menu.push(defaultRoute);
        }
    });

    const sidebarItems = roles_menu;

    return (
        <div className={`bg-gray-800 overflow-scroll overflow-x-hidden hide-scrollbar ${isMobile && 'w-full h-full fixed top-0 left-0 transform translate-x-0'} text-white w-64 space-y-6 py-6 px-2 inset-y-0 left-0 transform-translate-x-full md:translate-x-0 ${isMobile && isNavigationSidebarOpen ? 'hidden' : 'block'} ${isTablet && isNavigationSidebarOpen ? 'hidden' : "block"} ${isDesktop && !isNavigationSidebarOpen ? 'hidden' : "block"} transition-transform duration-200 ease-in-out`}>
            {isMobile && (
                <div className="flex justify-end">
                    <button
                        className="text-white"
                        onClick={() => setNavigationSidebarOpen(!isNavigationSidebarOpen)}
                    >
                        <Bars4Icon className="h-6 w-6" />
                    </button>
                </div>
            )}
            <a href="/"><img src={process.env.PUBLIC_URL + '/logo_transparent.jpeg'} className="m-0" alt="cheche logo" /></a>

            <ul className="space-y-3">
                {sidebarItems.map(item => (
                    (item.bool === undefined || item.bool === true) && (
                        <SidebarItem
                            key={item.to}
                            Icon={item.Icon}
                            label={t(item.label)}
                            to={item.to}
                            isActive={location.pathname === item.to}
                            onClick={handleLinkClick}
                            children={item.children}
                        />
                    )
                ))}
            </ul>
        </div>
    );
}
export default NavigationSidebar;